import { useState, useRef, useLayoutEffect } from "react";
import { User, Phone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { MEMBER_BADGES, DEFAULT_BADGE_KEY, getBadge, initialOf } from "../../utils/memberBadges";
import { useTranslation } from "@/i18n";

export const MemberSetupModal = () => {
  const { t } = useTranslation();
  const { showMemberSetup, completeMemberSetup, skipMemberSetup, user } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [badge, setBadge] = useState(DEFAULT_BADGE_KEY);
  const [loading, setLoading] = useState(false);
  const nameInputRef = useRef(null);

  useLayoutEffect(() => {
    if (showMemberSetup && nameInputRef.current) {
      requestAnimationFrame(() => {
        nameInputRef.current.focus();
      });
    }
  }, [showMemberSetup]);

  if (!showMemberSetup) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    setLoading(true);
    try {
      // completeMemberSetup returns false when the member was saved on this
      // phone but did NOT reach the backend. The setup used to close either
      // way, so the family list quietly lacked someone the user had just
      // added. (10 Sep 2026)
      const ok = await completeMemberSetup(name.trim(), phone.trim() || null, badge);
      if (!ok) toast.error(t("common.saveFailed"));
    } finally {
      setLoading(false);
    }
  };

  const selectedBadge = getBadge(badge);
  const previewInitial = initialOf(name) || "?";

  return (
    <div className="modal-overlay" data-testid="member-setup-modal">
      <div className="modal-content max-w-sm overflow-y-auto overscroll-contain" style={{ maxHeight: '70vh', WebkitOverflowScrolling: 'touch' }}>
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold">{t('modals.welcomeToFamily', { family: user?.family_name })}</h2>
          <p className="text-gray-500 text-sm mt-2">
            {t('modals.letFamilyKnow')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('settings.yourName')} *</label>
            <input
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('modals.namePlaceholder')}
              className="clean-input w-full"
              required
              data-testid="member-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">{t('modals.phoneNumber')}</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                className="clean-input w-full pl-10"
                data-testid="member-phone-input"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('settings.memberPhoneHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('modals.badgeColour')}</label>
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm"
                style={{ backgroundColor: selectedBadge.bg }}
                data-testid="badge-preview"
              >
                <span className="text-lg font-bold" style={{ color: selectedBadge.text }}>
                  {previewInitial}
                </span>
              </div>
              <p className="text-xs text-gray-400">{t('settings.memberBadgeHint')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {MEMBER_BADGES.map((b) => (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => setBadge(b.key)}
                  aria-label={b.label}
                  className={`w-9 h-9 rounded-full flex items-center justify-center transition-transform duration-100 ${
                    badge === b.key ? "ring-2 ring-offset-2 ring-gray-800 dark:ring-offset-gray-900 scale-110" : ""
                  }`}
                  style={{ backgroundColor: b.bg }}
                  data-testid={`badge-option-${b.key}`}
                >
                  {badge === b.key && (
                    <span className="text-sm font-bold" style={{ color: b.text }}>
                      {previewInitial}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={skipMemberSetup}
              className="clean-btn-secondary flex-1"
              data-testid="skip-member-setup-btn"
            >
              {t('common.skip')}
            </button>
            <button
              type="submit"
              disabled={!name.trim() || loading}
              className="clean-btn-primary flex-1"
              data-testid="save-member-setup-btn"
            >
              {loading ? t('common.saving') : t('modals.continueBtn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
